/**
 * DEPLOY-FRESHNESS-001 — the SPA knows which build it is and can ask its own server
 * which build is live.
 *
 * Every `vite build` mints one release id, bakes it into the bundle (`__APP_RELEASE_ID__`,
 * see vite.config.ts) and publishes it as `/version.json` next to index.html. Nginx
 * serves that file — and index.html — with `Cache-Control: no-cache`, so the answer is
 * always the live build. When the served id differs from the running one, a new build
 * is live: the App banner offers "Reload now" and the router turns the next in-app
 * navigation into a full page load (releaseNavigationGuard) so nobody has to hard-refresh.
 *
 * The old design compared against the API's /app-meta; that coupled the check to the
 * backend rollout and was never wired in deploy (both ids were empty). This one is
 * self-contained: the SPA image is the only source of truth for the SPA build.
 */
import { reactive } from 'vue'

export const RELEASE_MANIFEST_PATH = '/version.json'

/** Minimum gap between two update-driven full page loads (survives the reload). */
export const HARD_NAVIGATION_COOLDOWN_MS = 2 * 60 * 1000

/** Minimum gap between two manifest checks triggered by in-app navigation. */
export const NAVIGATION_CHECK_THROTTLE_MS = 60 * 1000

export const HARD_NAVIGATION_STORAGE_KEY = 'rukh_release_hard_nav_at'

export interface ReleaseManifest {
  release_id: string
  built_at?: string
}

export interface ReleaseState {
  /** Bundle id of the running app ('' when unknown — dev server without define, tests). */
  clientReleaseId: string
  /** Last id read from /version.json (null = not fetched yet / unavailable). */
  serverReleaseId: string | null
  /** True once the served build differs from the running one. */
  updateAvailable: boolean
  /** Epoch ms of the last manifest check (0 = never). */
  lastCheckedAt: number
}

/** Build id baked in by vite.config.ts `define`; absent under vitest / plain tsc. */
export function currentReleaseId(): string {
  try {
    return typeof __APP_RELEASE_ID__ === 'string' ? __APP_RELEASE_ID__.trim() : ''
  } catch {
    return ''
  }
}

export const releaseState: ReleaseState = reactive({
  clientReleaseId: currentReleaseId(),
  serverReleaseId: null,
  updateAvailable: false,
  lastCheckedAt: 0
})

/** Test seam: reset the singleton between cases. */
export function resetReleaseState(clientReleaseId = currentReleaseId()): void {
  releaseState.clientReleaseId = clientReleaseId
  releaseState.serverReleaseId = null
  releaseState.updateAvailable = false
  releaseState.lastCheckedAt = 0
}

/** A mismatch needs BOTH ids — an unknown side is never "an update". */
export function isUpdateAvailable(clientId: string, serverId: string | null): boolean {
  const c = clientId.trim()
  const s = (serverId ?? '').trim()
  return c !== '' && s !== '' && c !== s
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>

/**
 * GET /version.json from the SPA's own origin. Cache-busted twice (query + no-store) so
 * neither the browser nor an intermediary can answer from a stale copy. Any failure —
 * 404 on the dev server, network down, malformed body — resolves to null (unknown).
 */
export async function fetchServedReleaseId(fetchImpl: FetchLike = fetch): Promise<string | null> {
  try {
    const res = await fetchImpl(`${RELEASE_MANIFEST_PATH}?_=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
    if (!res.ok) return null
    const data = (await res.json()) as Partial<ReleaseManifest> | null
    const id = typeof data?.release_id === 'string' ? data.release_id.trim() : ''
    return id || null
  } catch {
    return null
  }
}

/**
 * Refresh the singleton from the served manifest. Skips the network when the running
 * build has no id (nothing to compare). Never clears `updateAvailable` once set — a
 * transient "unknown" answer must not hide a real update.
 */
export async function refreshReleaseState(
  fetchImpl: FetchLike = fetch,
  now: number = Date.now()
): Promise<void> {
  releaseState.lastCheckedAt = now
  if (!releaseState.clientReleaseId) return
  const served = await fetchServedReleaseId(fetchImpl)
  releaseState.serverReleaseId = served
  if (isUpdateAvailable(releaseState.clientReleaseId, served)) {
    releaseState.updateAvailable = true
  }
}

/** Navigation-driven check, throttled so busy users do not hammer the manifest. */
export function isNavigationCheckDue(state: ReleaseState, now: number): boolean {
  return now - state.lastCheckedAt >= NAVIGATION_CHECK_THROTTLE_MS
}

function readHardNavigationAt(): number {
  try {
    const raw = sessionStorage.getItem(HARD_NAVIGATION_STORAGE_KEY)
    const n = raw ? Number(raw) : 0
    return Number.isFinite(n) ? n : 0
  } catch {
    return 0
  }
}

export function markHardNavigation(now: number): void {
  try {
    sessionStorage.setItem(HARD_NAVIGATION_STORAGE_KEY, String(now))
  } catch {
    /* private mode / quota — cooldown then lives only in the banner path */
  }
}

/**
 * Turn the next in-app navigation into a full page load? Only while an update is
 * pending, and at most once per cooldown: during a rolling deploy two pods can serve
 * different builds for a minute, and we must not bounce the user between them — after
 * one full load the banner takes over.
 */
export function shouldHardNavigate(state: ReleaseState, now: number): boolean {
  if (!state.updateAvailable) return false
  return now - readHardNavigationAt() >= HARD_NAVIGATION_COOLDOWN_MS
}

/** Absolute href for a router fullPath under the app's history base. */
export function hardNavigationHref(fullPath: string, base: string = ''): string {
  const trimmedBase = base.replace(/\/+$/, '')
  const path = fullPath.startsWith('/') ? fullPath : `/${fullPath}`
  return `${trimmedBase}${path}`
}

/** Indirection so tests can observe the full load without touching window.location. */
export const hardNavigationExecutor = {
  execute(href: string): void {
    window.location.assign(href)
  }
}
